import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Company, CompanyFormData } from '../models/CompanyModel';
import { getCompanies, getCompany, createCompany as createCompanyAPI, updateCompany as updateCompanyAPI, deleteCompany as deleteCompanyAPI } from './CompanyCRUD';

const normalizeCompaniesPayload = (payload: unknown): Company[] => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (payload && typeof payload === 'object' && Array.isArray((payload as { results?: unknown }).results)) {
    return (payload as { results: Company[] }).results;
  }

  return [];
};

// Async thunks
export const fetchCompanies = createAsyncThunk(
  'company/fetchCompanies',
  async () => {
    const response = await getCompanies();
    return normalizeCompaniesPayload(response.data);
  }
);

export const fetchCompanyById = createAsyncThunk(
  'company/fetchCompanyById',
  async (id: string) => {
    const response = await getCompany(id);
    return response.data;
  }
);

export const createCompany = createAsyncThunk(
  'company/createCompany',
  async (data: CompanyFormData) => {
    const response = await createCompanyAPI(data);
    return response.data;
  }
);

export const updateCompany = createAsyncThunk(
  'company/updateCompany',
  async ({ id, data }: { id: string; data: CompanyFormData }) => {
    const response = await updateCompanyAPI(id, data);
    return response.data;
  }
);

export const deleteCompany = createAsyncThunk(
  'company/deleteCompany',
  async (id: string) => {
    await deleteCompanyAPI(id);
    return id;
  }
);

// State interface
interface CompanyState {
  companies: Company[];
  selectedCompany: Company | null;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: CompanyState = {
  companies: [],
  selectedCompany: null,
  loading: false,
  error: null,
};

// Slice
const companySlice = createSlice({
  name: 'company',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedCompany: (state) => {
      state.selectedCompany = null;
    },
    setSelectedCompany: (state, action: PayloadAction<Company>) => {
      state.selectedCompany = action.payload;
    },
    clearRequestLoading: (state) => {
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    // Fetch companies
    builder
      .addCase(fetchCompanies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompanies.fulfilled, (state, action) => {
        state.loading = false;
        state.companies = normalizeCompaniesPayload(action.payload);
      })
      .addCase(fetchCompanies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch companies';
      });

    // Fetch company by ID
    builder
      .addCase(fetchCompanyById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompanyById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedCompany = action.payload;
      })
      .addCase(fetchCompanyById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch company';
      });

    // Create company
    builder
      .addCase(createCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCompany.fulfilled, (state, action) => {
        state.loading = false;
        state.companies.push(action.payload);
      })
      .addCase(createCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create company';
      });

    // Update company
    builder
      .addCase(updateCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCompany.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.companies.findIndex(company => company.id === action.payload.id);
        if (index !== -1) {
          state.companies[index] = action.payload;
        }
        if (state.selectedCompany?.id === action.payload.id) {
          state.selectedCompany = action.payload;
        }
      })
      .addCase(updateCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update company';
      });

    // Delete company
    builder
      .addCase(deleteCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCompany.fulfilled, (state, action) => {
        state.loading = false;
        state.companies = state.companies.filter(company => company.id !== action.payload);
        if (state.selectedCompany?.id === action.payload) {
          state.selectedCompany = null;
        }
      })
      .addCase(deleteCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete company';
      });
  },
});

// Export actions
export const { clearError, clearSelectedCompany, setSelectedCompany, clearRequestLoading } = companySlice.actions;

// Export reducer
export default companySlice.reducer;

// Export selectors
export const selectCompanies = (state: { company: CompanyState }) => normalizeCompaniesPayload(state.company.companies);
export const selectSelectedCompany = (state: { company: CompanyState }) => state.company.selectedCompany;
export const selectCompanyLoading = (state: { company: CompanyState }) => state.company.loading;
export const selectCompanyError = (state: { company: CompanyState }) => state.company.error; 